import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Search, FileText, Tag, Send, Phone, User, Building2, ChevronRight, CheckCircle2, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShareDocument = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Parse pre-selected doc from URL params (when coming from Documents page)
  const urlParams = new URLSearchParams(location.search);
  const preDocId = urlParams.get('docId') || '';
  const preDocName = urlParams.get('docName') || '';

  // Step tracker: 1 = pick doc, 2 = pick contact, 3 = confirm & send
  const [step, setStep] = useState(preDocId ? 2 : 1);

  // Step 1 — Document selection
  const [documents, setDocuments] = useState([]);
  const [docSearch, setDocSearch] = useState('');
  const [selectedDoc, setSelectedDoc] = useState(
    preDocId ? { id: preDocId, name: preDocName } : null
  );
  const [loadingDocs, setLoadingDocs] = useState(true);

  // Step 2 — Account / Contact selection
  const [accountSearch, setAccountSearch] = useState('');
  const [accountResults, setAccountResults] = useState([]);
  const [searchingAccounts, setSearchingAccounts] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);

  // Step 3 — Custom message
  const [customMessage, setCustomMessage] = useState('');

  useEffect(() => {
    fetchDocuments();
  }, []);

  useEffect(() => {
    if (preDocId) {
      // Fetch the pre-selected doc details
      fetchDocuments();
    }
  }, [preDocId]);

  useEffect(() => {
    const searchAccounts = async () => {
      if (accountSearch.length < 2) { setAccountResults([]); return; }
      setSearchingAccounts(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/accounts/search?q=${encodeURIComponent(accountSearch)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAccountResults(response.data);
      } catch (error) {
        console.error('Account search failed:', error);
      } finally {
        setSearchingAccounts(false);
      }
    };
    const debounce = setTimeout(searchAccounts, 300);
    return () => clearTimeout(debounce);
  }, [accountSearch]);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (docSearch) params.q = docSearch;
      const response = await axios.get(`${API}/documents`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
      // If pre-selected doc, enrich its details from the list
      if (preDocId) {
        const found = response.data.find(d => d.id === preDocId);
        if (found) setSelectedDoc(found);
      }
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoadingDocs(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [docSearch]);

  const handleSelectDoc = (doc) => {
    setSelectedDoc(doc);
    setStep(2);
  };

  const handleSelectContact = (account, contact) => {
    setSelectedAccount(account);
    setSelectedContact(contact);
    // Build default message
    setCustomMessage(
      `Hi Dr. ${contact.name}, hope you are doing well!\n\nI wanted to share the following document with you: *${selectedDoc.name}*\n\nPlease find it here: ${BACKEND_URL}/api/documents/${selectedDoc.id}/download\n\nLooking forward to your feedback!\n\nRegards,\n${localStorage.getItem('userName') || 'VoiceCRM'}`
    );
    setStep(3);
  };

  const handleSendWhatsApp = () => {
    if (!selectedContact?.whatsapp_number) {
      toast.error('This contact has no WhatsApp number saved');
      return;
    }

    // Clean the phone number — remove spaces, dashes, +
    let phone = selectedContact.whatsapp_number.replace(/[\s\-\(\)]/g, '');
    // Add India country code if not present
    if (!phone.startsWith('+') && !phone.startsWith('91') && phone.length === 10) {
      phone = '91' + phone;
    }
    phone = phone.replace('+', '');

    const encodedMessage = encodeURIComponent(customMessage);
    const waUrl = `https://wa.me/${phone}?text=${encodedMessage}`;

    // Open WhatsApp
    window.open(waUrl, '_blank');
    toast.success(`Opening WhatsApp for Dr. ${selectedContact.name}!`);
  };

  const getFileIcon = (fileType) => {
    if (!fileType) return '📎';
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('image')) return '🖼️';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('presentation') || fileType.includes('powerpoint')) return '📊';
    return '📎';
  };

  return (
    <Layout onLogout={onLogout}>
      <div className="p-8 max-w-3xl" data-testid="share-document-page">

        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/documents')}
            className="flex items-center gap-2 text-slate-500 hover:text-teal-700 transition-colors mb-4 text-sm font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Documents
          </button>
          <h1 className="text-4xl font-bold tracking-tight text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Share via WhatsApp
          </h1>
          <p className="text-base text-slate-500 mt-2">
            Send a document directly to a doctor's WhatsApp in seconds
          </p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center gap-2 mb-8">
          {[
            { num: 1, label: 'Pick Document' },
            { num: 2, label: 'Pick Doctor' },
            { num: 3, label: 'Send' }
          ].map((s, idx) => (
            <React.Fragment key={s.num}>
              <div className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm transition-all ${
                step === s.num
                  ? 'bg-gradient-to-r from-teal-700 to-emerald-500 text-white shadow-lg shadow-teal-500/25'
                  : step > s.num
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-slate-100 text-slate-400'
              }`}>
                {step > s.num
                  ? <CheckCircle2 className="h-4 w-4" />
                  : <span className="h-5 w-5 rounded-full border-2 flex items-center justify-center text-xs font-bold border-current">{s.num}</span>
                }
                {s.label}
              </div>
              {idx < 2 && <ChevronRight className="h-4 w-4 text-slate-300" />}
            </React.Fragment>
          ))}
        </div>

        {/* ── STEP 1: Pick Document ── */}
        {step === 1 && (
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 shadow-xl p-6">
            <h2 className="text-lg font-bold text-slate-800 mb-4">Which document do you want to share?</h2>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                value={docSearch}
                onChange={(e) => setDocSearch(e.target.value)}
                placeholder="Search documents..."
                className="pl-10 h-12 rounded-xl"
              />
            </div>
            {loadingDocs ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-4 border-teal-500 border-t-transparent"></div>
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <FileText className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p>No documents found. Upload some first.</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    onClick={() => handleSelectDoc(doc)}
                    className="flex items-center gap-4 p-4 border border-slate-200 rounded-xl hover:border-teal-300 hover:bg-teal-50/30 cursor-pointer transition-all group"
                  >
                    <div className="text-2xl">{getFileIcon(doc.file_type)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 group-hover:text-teal-700">{doc.name}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {doc.product_tags?.map((tag, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 bg-teal-100 text-teal-700 rounded-md">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-teal-600" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── STEP 2: Pick Doctor / Contact ── */}
        {step === 2 && (
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 shadow-xl p-6">

            {/* Selected doc reminder */}
            <div className="flex items-center gap-3 p-3 bg-teal-50 border border-teal-100 rounded-xl mb-5">
              <div className="text-xl">{getFileIcon(selectedDoc?.file_type)}</div>
              <div className="flex-1">
                <p className="text-xs text-teal-600 font-medium">Sharing:</p>
                <p className="font-semibold text-teal-800">{selectedDoc?.name}</p>
              </div>
              <button onClick={() => setStep(1)} className="text-xs text-violet-400 hover:text-teal-700 underline">
                Change
              </button>
            </div>

            <h2 className="text-lg font-bold text-slate-800 mb-4">Which doctor do you want to send it to?</h2>

            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                value={accountSearch}
                onChange={(e) => setAccountSearch(e.target.value)}
                placeholder="Type doctor or clinic name..."
                className="pl-10 h-12 rounded-xl"
                autoFocus
              />
            </div>

            {searchingAccounts && (
              <div className="text-center py-4">
                <div className="inline-block animate-spin rounded-full h-5 w-5 border-4 border-teal-500 border-t-transparent"></div>
              </div>
            )}

            {accountResults.length > 0 && (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {accountResults.map((account) => (
                  <div key={account.id} className="border border-slate-200 rounded-xl overflow-hidden">
                    {/* Account header */}
                    <div className="flex items-center gap-3 p-3 bg-slate-50 border-b border-slate-100">
                      <Building2 className="h-4 w-4 text-teal-600" />
                      <p className="font-semibold text-slate-700 text-sm">{account.name}</p>
                    </div>
                    {/* Contacts */}
                    {account.contacts?.length > 0 ? (
                      account.contacts.map((contact) => (
                        <div
                          key={contact.id}
                          onClick={() => handleSelectContact(account, contact)}
                          className="flex items-center gap-3 p-3 hover:bg-emerald-50 cursor-pointer transition-colors group border-b border-slate-50 last:border-0"
                        >
                          <div className="p-1.5 bg-emerald-100 rounded-lg">
                            <User className="h-4 w-4 text-emerald-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-slate-800 group-hover:text-emerald-700 text-sm">{contact.name}</p>
                            <p className="text-xs text-slate-400">{contact.designation}</p>
                          </div>
                          {contact.whatsapp_number ? (
                            <div className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                              <Phone className="h-3.5 w-3.5" />
                              {contact.whatsapp_number}
                            </div>
                          ) : (
                            <span className="text-xs text-red-400">No WhatsApp</span>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="p-3 text-xs text-slate-400">No contacts for this account</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {accountSearch.length >= 2 && !searchingAccounts && accountResults.length === 0 && (
              <div className="text-center py-6 text-slate-400 text-sm">
                No accounts found for "{accountSearch}"
              </div>
            )}

            {accountSearch.length < 2 && (
              <p className="text-center text-slate-400 text-sm py-4">
                Type at least 2 characters to search
              </p>
            )}
          </div>
        )}

        {/* ── STEP 3: Confirm & Send ── */}
        {step === 3 && (
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 shadow-xl p-6 space-y-5">

            {/* Summary cards */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-teal-50 border border-teal-100 rounded-xl">
                <p className="text-xs text-teal-600 font-medium mb-1">Document</p>
                <div className="flex items-center gap-2">
                  <span className="text-lg">{getFileIcon(selectedDoc?.file_type)}</span>
                  <p className="font-semibold text-teal-800 text-sm truncate">{selectedDoc?.name}</p>
                </div>
                <button onClick={() => { setStep(1); setSelectedContact(null); }} className="text-xs text-violet-400 hover:text-teal-700 underline mt-1">
                  Change
                </button>
              </div>
              <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl">
                <p className="text-xs text-emerald-500 font-medium mb-1">Sending to</p>
                <p className="font-semibold text-emerald-800 text-sm">{selectedContact?.name}</p>
                <p className="text-xs text-emerald-600">{selectedAccount?.name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{selectedContact?.whatsapp_number}</p>
                <button onClick={() => { setStep(2); setSelectedContact(null); }} className="text-xs text-emerald-400 hover:text-emerald-600 underline mt-1">
                  Change
                </button>
              </div>
            </div>

            {/* Editable Message */}
            <div>
              <Label className="text-sm font-medium text-slate-700">Message (editable)</Label>
              <textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                rows={8}
                className="mt-2 w-full p-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 text-sm text-slate-700 resize-none outline-none"
              />
              <p className="text-xs text-slate-400 mt-1">
                💡 You can edit this message before sending. The download link is included automatically.
              </p>
            </div>

            {/* Send Button */}
            <Button
              onClick={handleSendWhatsApp}
              disabled={!selectedContact?.whatsapp_number}
              className="w-full h-14 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl font-bold text-base shadow-lg shadow-emerald-500/25"
            >
              <Send className="mr-3 h-5 w-5" />
              Open WhatsApp &amp; Send to Dr. {selectedContact?.name}
            </Button>

            {!selectedContact?.whatsapp_number && (
              <p className="text-center text-sm text-red-500">
                ⚠️ This contact has no WhatsApp number. Please update their profile first.
              </p>
            )}
          </div>
        )}

      </div>
    </Layout>
  );
};

export default ShareDocument;
